import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";

import Modal from "../../layouts/Modal";
import { TextInput } from "../shared/Inputs/TextInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";

import useWilmaUsers from "../../data/useWilmaUsers";
import useCustomerRoles from "../../data/useCustomerRoles";
import { IUserInviteProps } from "../../models/auth/WilmaUser";
import RoleSelect, { defaultRoleValue } from "./RoleSelect";


export default function AdminUserInviteModal({ open, setOpen }: { open: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>> }) {
    const { saveHandlers } = useDefaultCRUDHandlers("Users");
    const { inviteWilmaUser } = useWilmaUsers();
    const { roles } = useCustomerRoles();
    const customerRoles = roles.data?.data ?? [];
    const [selectedRoleId, setSelectedRoleId] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    const { handleSubmit, register, reset } = useForm<IUserInviteProps>({
        defaultValues: {
            email: "",
            forename: "",
            surname: "",
            password: "",
            username: "",
        }
      });

    useEffect(() => {
        if (!open) return;

        reset({ email: "", forename: "", surname: "", password: "", username: "" });
        setSelectedRoleId("");
        setError(null);
    }, [open, reset]);

    // Default to Staff once roles are in, without undoing a choice the admin already made
    useEffect(() => {
        if (open && selectedRoleId === "") setSelectedRoleId(defaultRoleValue(roles.data?.data ?? []));
    }, [open, roles.data, selectedRoleId]);

    const onSubmit = (data: IUserInviteProps) => {
        if (customerRoles.length > 0 && !selectedRoleId) {
            setError("Please choose a role");
            return;
        }

        setError(null);

        inviteWilmaUser.mutate({
                ...data,
                username: data.email,
                password: "password",
                roleId: selectedRoleId || null,
            }, {
            onSuccess: () => {
                setOpen(false);
                saveHandlers.onSuccess();
            },
            onError: (error: any) => {
                console.error(error);
                setError(error?.message || "Failed to invite user");
            }
        });
    }

    return (
        <Modal width="max-w-6xl" open={open} setOpen={setOpen} >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-4">
                <h1 className="text-xl">Invite a new user</h1>
                <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">
                    <TextInput label="First Name" registerName={"forename"} register={register}/>
                    <TextInput label="Last Name" registerName={"surname"} register={register}/>
                    <TextInput label="Email" registerName={"email"} register={register}/>
                    <RoleSelect
                        roles={customerRoles}
                        value={selectedRoleId}
                        onChange={setSelectedRoleId}
                        disabled={roles.isLoading}
                        hint={roles.isError ? "Couldn't load roles." : undefined}
                    />
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <ButtonInput label={inviteWilmaUser.isLoading ? "Inviting..." : "Invite User"} isSubmit disabled={inviteWilmaUser.isLoading} />
            </form>
        </Modal>
    )
}
